import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, Button, Form, Table, Row, Col, Badge, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import {
    getSettlements,
    getSettlementTrips,
    getSettlementAdvances,
    uploadSettlementInvoice
} from '../api';
import { generateSettlementPDF } from '../utils/SettlementPDF';
import { generateSettlementExcel } from '../utils/SettlementExcel';
import {
    DollarSign,
    Calendar,
    ChevronDown,
    ChevronUp,
    CheckCircle,
    Clock,
    Search,
    List,
    CreditCard,
    User
} from 'lucide-react';

const ProviderSettlementsView = ({ providerId = null, drivers = [], providerDetails = null }) => {
    // Por defecto mostramos los últimos 60 días para que el proveedor vea sus liquidaciones de inmediato
    const [startDate, setStartDate] = useState(() => {
        const d = new Date();
        d.setDate(d.getDate() - 60);
        return d.toISOString().slice(0, 10);
    });
    const [endDate, setEndDate] = useState(() => new Date().toISOString().slice(0, 10));
    const [paymentStatusFilter, setPaymentStatusFilter] = useState('all'); // 'all' | 'paid' | 'unpaid'
    const [selectedDriverId, setSelectedDriverId] = useState('');
    const [searchSettlementFolio, setSearchSettlementFolio] = useState('');

    const [results, setResults] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    // Detalle desplegable
    const [expandedRow, setExpandedRow] = useState(null);
    const [rowTrips, setRowTrips] = useState({});
    const [rowAdvances, setRowAdvances] = useState({});
    const [loadingTrips, setLoadingTrips] = useState({});
    const [uploadingInvoiceId, setUploadingInvoiceId] = useState(null);
    const [downloadingId, setDownloadingId] = useState(null);

    const defaultCurrency = useMemo(() => {
        if (results.length > 0 && results[0].currency_code) {
            return results[0].currency_code;
        }
        return providerDetails?.currency_code || 'CLP';
    }, [results, providerDetails]);

    const formatCurrency = useCallback((val, currency = defaultCurrency) => {
        const currUpper = String(currency || 'CLP').toUpperCase();
        const num = Math.round(Number(val) || 0);
        if (currUpper === 'PEN') return `S/. ${num.toLocaleString()}`;
        if (currUpper === 'USD') return `US$ ${num.toLocaleString()}`;
        if (currUpper === 'ARS') return `ARS $${num.toLocaleString()}`;
        if (currUpper === 'BRL') return `R$ ${num.toLocaleString()}`;
        if (currUpper === 'DOP') return `RD$ ${num.toLocaleString()}`;
        if (currUpper === 'EUR') return `€ ${num.toLocaleString()}`;
        return `$${num.toLocaleString()}`;
    }, [defaultCurrency]);

    const handleSearch = useCallback(async () => {
        setIsLoading(true);
        setExpandedRow(null);
        try {
            const folioClean = searchSettlementFolio.trim().replace(/^M-0*/i, '');
            const folioNum = folioClean !== '' ? parseInt(folioClean, 10) : null;

            const queryParams = {};
            if (providerId) {
                queryParams.provider_id = providerId;
            }
            if (folioNum && !isNaN(folioNum)) {
                queryParams.folio = folioNum;
            } else {
                if (startDate) queryParams.start_date = startDate;
                if (endDate) queryParams.end_date = endDate;
                if (paymentStatusFilter === 'paid') queryParams.is_paid = true;
                if (paymentStatusFilter === 'unpaid') queryParams.is_paid = false;
                if (selectedDriverId) queryParams.driver_id = selectedDriverId;
            }

            const data = await getSettlements(queryParams);
            setResults(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error('Error cargando liquidaciones del proveedor:', err);
            toast.error('No se pudieron cargar las liquidaciones.');
        } finally {
            setIsLoading(false);
        }
    }, [providerId, startDate, endDate, paymentStatusFilter, selectedDriverId, searchSettlementFolio]);

    useEffect(() => {
        handleSearch();
    }, [handleSearch]);

    const toggleRow = async (settlement) => {
        const id = settlement.id;
        if (expandedRow === id) {
            setExpandedRow(null);
            return;
        }
        setExpandedRow(id);

        if (!rowTrips[id]) {
            setLoadingTrips(prev => ({ ...prev, [id]: true }));
            try {
                const tripsData = await getSettlementTrips(id);
                let advancesData = [];
                try {
                    advancesData = await getSettlementAdvances(id);
                } catch (advErr) {
                    console.warn('Error cargando descuentos de liquidación:', advErr);
                }
                setRowTrips(prev => ({ ...prev, [id]: Array.isArray(tripsData) ? tripsData : [] }));
                setRowAdvances(prev => ({ ...prev, [id]: Array.isArray(advancesData) ? advancesData : [] }));
            } catch (err) {
                console.error('Error cargando detalle de liquidación:', err);
                toast.error('Error al cargar los viajes de esta liquidación.');
            } finally {
                setLoadingTrips(prev => ({ ...prev, [id]: false }));
            }
        }
    };

    const downloadDocs = async (settlement, type) => {
        setDownloadingId(`${settlement.id}-${type}`);
        try {
            let trips = rowTrips[settlement.id];
            let advances = rowAdvances[settlement.id];
            if (!trips) {
                trips = await getSettlementTrips(settlement.id);
                try {
                    advances = await getSettlementAdvances(settlement.id);
                } catch (e) {
                    advances = [];
                }
                setRowTrips(prev => ({ ...prev, [settlement.id]: Array.isArray(trips) ? trips : [] }));
                setRowAdvances(prev => ({ ...prev, [settlement.id]: Array.isArray(advances) ? advances : [] }));
            }
            const sortedTrips = [...(trips || [])].sort((a, b) => new Date(a.scheduled_time) - new Date(b.scheduled_time));
            if (type === 'pdf') {
                await generateSettlementPDF(settlement, sortedTrips, advances || []);
            } else {
                generateSettlementExcel(settlement, sortedTrips, advances || []);
            }
        } catch (err) {
            console.error('Error descargando documento de liquidación:', err);
            toast.error('Error al generar el documento de la liquidación.');
        } finally {
            setDownloadingId(null);
        }
    };

    const handleInvoiceUpload = async (settlementId, e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setUploadingInvoiceId(settlementId);
        try {
            const updatedSettlement = await uploadSettlementInvoice(settlementId, file);
            toast.success(`🧾 Factura subida con éxito. Folio detectado: ${updatedSettlement.invoice_number || 'Registrada'}`);
            setResults(prev =>
                prev.map(item =>
                    item.id === settlementId
                        ? {
                              ...item,
                              invoice_number: updatedSettlement.invoice_number,
                              invoice_url: updatedSettlement.invoice_url
                          }
                        : item
                )
            );
        } catch (err) {
            console.error('Error al subir factura:', err);
            toast.error(err.message || 'Error al subir y procesar la factura.');
        } finally {
            setUploadingInvoiceId(null);
            e.target.value = '';
        }
    };

    const getSettleTotals = (item, trips, advances = []) => {
        let totalFacturarChofer = 0;
        let totalEfectivo = 0;
        let totalCostoProv = 0;

        const tripsList = Array.isArray(trips) ? trips : [];
        const currency =
            tripsList.length > 0
                ? (tripsList[0].provider_currency_code || tripsList[0].local_currency_code || item.currency_code || defaultCurrency)
                : (item.currency_code || defaultCurrency);

        tripsList.forEach(t => {
            if (!t) return;
            const waitCosto = parseFloat(t.waiting_time_provider_cost) || 0;
            const cBase = (currency !== 'CLP' ? (t.provider_cost_local || 0) : (t.provider_cost_base || 0)) + waitCosto;

            let cPago = t.driver_earnings || cBase || 0;
            const extraServices = Array.isArray(t.extra_services) ? t.extra_services : [];
            const reimbursable = extraServices.find(s => s && s.id === 'reimbursable_expense');
            if (reimbursable) {
                cPago += parseFloat(reimbursable.cost) || 0;
            }

            totalFacturarChofer += cPago;
            totalCostoProv += cBase;

            if (t.payment_method?.toLowerCase() === 'cash' || t.payment_method?.toLowerCase() === 'efectivo') {
                totalEfectivo += cBase;
            }
        });

        const liquido = (item.total_amount !== undefined && item.total_amount !== null)
            ? item.total_amount
            : Math.max(0, totalFacturarChofer - totalEfectivo);

        let anticipos = 0;
        let abonos = 0;
        if (Array.isArray(advances) && advances.length > 0) {
            advances.forEach(a => {
                const amt = parseFloat(a?.amount) || 0;
                if (amt > 0) anticipos += amt;
                else if (amt < 0) abonos += Math.abs(amt);
            });
        } else {
            const netDiff = (totalFacturarChofer - totalEfectivo) - liquido;
            if (netDiff > 0) anticipos = netDiff;
            else if (netDiff < 0) abonos = Math.abs(netDiff);
        }

        return {
            bruto: totalFacturarChofer,
            efectivo: totalEfectivo,
            anticipos,
            abonos,
            liquido,
            currency,
            costoProveedor: totalCostoProv
        };
    };

    const kpis = useMemo(() => {
        const totalAmount = results.reduce((sum, s) => sum + (Number(s.total_amount) || 0), 0);
        const paidItems = results.filter(s => s.is_paid);
        const unpaidItems = results.filter(s => !s.is_paid);
        const paidAmount = paidItems.reduce((sum, s) => sum + (Number(s.total_amount) || 0), 0);
        const unpaidAmount = unpaidItems.reduce((sum, s) => sum + (Number(s.total_amount) || 0), 0);
        return {
            totalAmount,
            totalCount: results.length,
            paidAmount,
            paidCount: paidItems.length,
            unpaidAmount,
            unpaidCount: unpaidItems.length
        };
    }, [results]);

    return (
        <div>
            {/* KPIs Superiores */}
            <Row className="mb-4 g-3">
                <Col md={4}>
                    <Card className="border-0 shadow-sm rounded-4 overflow-hidden">
                        <Card.Body className="p-3 d-flex align-items-center">
                            <div className="bg-primary bg-opacity-10 p-3 rounded-4 text-primary me-3">
                                <DollarSign size={26} />
                            </div>
                            <div>
                                <small className="text-muted fw-bold text-uppercase" style={{ fontSize: '11px', letterSpacing: '0.5px' }}>
                                    Total Liquidado ({kpis.totalCount})
                                </small>
                                <h4 className="mb-0 fw-bold">{formatCurrency(kpis.totalAmount)}</h4>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={4}>
                    <Card className="border-0 shadow-sm rounded-4 overflow-hidden">
                        <Card.Body className="p-3 d-flex align-items-center">
                            <div className="bg-success bg-opacity-10 p-3 rounded-4 text-success me-3">
                                <CheckCircle size={26} />
                            </div>
                            <div>
                                <small className="text-muted fw-bold text-uppercase" style={{ fontSize: '11px', letterSpacing: '0.5px' }}>
                                    Pagadas ({kpis.paidCount})
                                </small>
                                <h4 className="mb-0 fw-bold text-success">{formatCurrency(kpis.paidAmount)}</h4>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={4}>
                    <Card className="border-0 shadow-sm rounded-4 overflow-hidden">
                        <Card.Body className="p-3 d-flex align-items-center">
                            <div className="bg-warning bg-opacity-10 p-3 rounded-4 text-warning me-3">
                                <Clock size={26} />
                            </div>
                            <div>
                                <small className="text-muted fw-bold text-uppercase" style={{ fontSize: '11px', letterSpacing: '0.5px' }}>
                                    Pendientes de Pago ({kpis.unpaidCount})
                                </small>
                                <h4 className="mb-0 fw-bold text-warning">{formatCurrency(kpis.unpaidAmount)}</h4>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Panel de Filtros */}
            <Card className="border-0 shadow-sm rounded-4 mb-4">
                <Card.Body className="p-3">
                    <Row className="g-3 align-items-end">
                        <Col xs={6} md={2}>
                            <Form.Label className="small fw-bold text-muted mb-1">
                                <Calendar size={14} className="me-1" /> Desde
                            </Form.Label>
                            <Form.Control
                                type="date"
                                value={startDate}
                                onChange={e => setStartDate(e.target.value)}
                                disabled={searchSettlementFolio.trim() !== ''}
                            />
                        </Col>
                        <Col xs={6} md={2}>
                            <Form.Label className="small fw-bold text-muted mb-1">
                                <Calendar size={14} className="me-1" /> Hasta
                            </Form.Label>
                            <Form.Control
                                type="date"
                                value={endDate}
                                onChange={e => setEndDate(e.target.value)}
                                disabled={searchSettlementFolio.trim() !== ''}
                            />
                        </Col>
                        <Col xs={12} md={3}>
                            <Form.Label className="small fw-bold text-muted mb-1">
                                <User size={14} className="me-1" /> Conductor
                            </Form.Label>
                            <Form.Select
                                value={selectedDriverId}
                                onChange={e => setSelectedDriverId(e.target.value)}
                                disabled={searchSettlementFolio.trim() !== ''}
                            >
                                <option value="">Todos mis conductores</option>
                                {drivers.map(d => (
                                    <option key={d.id} value={d.id}>
                                        {d.full_name}
                                    </option>
                                ))}
                            </Form.Select>
                        </Col>
                        <Col xs={6} md={2}>
                            <Form.Label className="small fw-bold text-muted mb-1">
                                <CreditCard size={14} className="me-1" /> Estado Pago
                            </Form.Label>
                            <Form.Select
                                value={paymentStatusFilter}
                                onChange={e => setPaymentStatusFilter(e.target.value)}
                                disabled={searchSettlementFolio.trim() !== ''}
                            >
                                <option value="all">Todas</option>
                                <option value="paid">✅ Pagadas</option>
                                <option value="unpaid">⏳ Pendientes de Pago</option>
                            </Form.Select>
                        </Col>
                        <Col xs={6} md={2}>
                            <Form.Label className="small fw-bold text-muted mb-1">🔍 Nº Liquidación</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="Ej: 28 o M-028"
                                value={searchSettlementFolio}
                                onChange={e => setSearchSettlementFolio(e.target.value)}
                                onKeyDown={e => e.key === 'Enter' && handleSearch()}
                            />
                        </Col>
                        <Col xs={12} md={1} className="d-grid">
                            <Button variant="primary" className="fw-bold" onClick={handleSearch} disabled={isLoading}>
                                {isLoading ? <Spinner size="sm" /> : <Search size={18} />}
                            </Button>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            {/* Tabla de Liquidaciones */}
            <Card className="border-0 shadow-sm rounded-4 overflow-hidden">
                <Card.Header className="bg-white p-3 border-bottom d-flex justify-content-between align-items-center flex-wrap gap-2">
                    <div className="d-flex align-items-center gap-2">
                        <List size={18} className="text-primary" />
                        <span className="fw-bold">Liquidaciones Emitidas a tus Conductores</span>
                    </div>
                    <small className="text-muted">
                        Haz clic en una fila para ver el desglose de reservas, o descarga el comprobante en PDF / Excel.
                    </small>
                </Card.Header>
                <Card.Body className="p-0">
                    {isLoading ? (
                        <div className="text-center py-5">
                            <Spinner animation="border" variant="primary" />
                            <div className="mt-2 text-muted small">Cargando liquidaciones...</div>
                        </div>
                    ) : results.length === 0 ? (
                        <div className="text-center py-5 text-muted">
                            No se encontraron liquidaciones emitidas para los filtros seleccionados.
                        </div>
                    ) : (
                        <Table hover responsive className="mb-0 align-middle">
                            <thead className="bg-light border-bottom">
                                <tr>
                                    <th style={{ width: '40px' }}></th>
                                    <th className="py-3 px-3 text-muted small text-uppercase fw-bold">Folio</th>
                                    <th className="py-3 text-muted small text-uppercase fw-bold">Emisión</th>
                                    <th className="py-3 text-muted small text-uppercase fw-bold">Período Liquidado</th>
                                    <th className="py-3 text-muted small text-uppercase fw-bold">Conductor</th>
                                    <th className="py-3 text-center text-muted small text-uppercase fw-bold">Viajes</th>
                                    <th className="py-3 text-end text-muted small text-uppercase fw-bold">Total Líquido</th>
                                    <th className="py-3 text-center text-muted small text-uppercase fw-bold">Estado / Factura</th>
                                    <th className="py-3 text-center text-muted small text-uppercase fw-bold pe-3">Descargar / Factura</th>
                                </tr>
                            </thead>
                            <tbody>
                                {results.map(item => {
                                    const isExpanded = expandedRow === item.id;
                                    return (
                                        <React.Fragment key={item.id}>
                                            <tr
                                                onClick={() => toggleRow(item)}
                                                style={{ cursor: 'pointer', transition: 'all 0.15s' }}
                                                className={isExpanded ? 'table-primary bg-opacity-10' : ''}
                                            >
                                                <td className="ps-3 text-center">
                                                    {isExpanded ? <ChevronUp size={18} className="text-primary" /> : <ChevronDown size={18} className="text-muted" />}
                                                </td>
                                                <td className="px-3 fw-bold text-primary">
                                                    M-{String(item.id).padStart(3, '0')}
                                                </td>
                                                <td className="small text-muted">
                                                    {item.created_at ? new Date(item.created_at).toLocaleDateString('es-CL') : '-'}
                                                </td>
                                                <td className="small">
                                                    <span className="fw-semibold">{item.period_start}</span> al <span className="fw-semibold">{item.period_end}</span>
                                                </td>
                                                <td className="fw-bold">
                                                    {item.driver?.full_name || `Conductor #${item.driver_id}`}
                                                </td>
                                                <td className="text-center">
                                                    <Badge bg="light" text="dark" className="border px-2 py-1">
                                                        {item.total_trips}
                                                    </Badge>
                                                </td>
                                                <td className="text-end fw-bold text-success fs-6">
                                                    {formatCurrency(item.total_amount, item.currency_code)}
                                                </td>
                                                <td className="text-center">
                                                    {item.is_paid ? (
                                                        <Badge bg="success" className="bg-opacity-10 text-success border border-success border-opacity-25">
                                                            PAGADA
                                                        </Badge>
                                                    ) : (
                                                        <Badge bg="warning" text="dark" className="bg-opacity-25 border border-warning">
                                                            PENDIENTE DE PAGO
                                                        </Badge>
                                                    )}
                                                    {item.invoice_number && (
                                                        <div className="small text-muted font-monospace mt-1">
                                                            Factura: #{item.invoice_number}
                                                        </div>
                                                    )}
                                                    {item.notes && (() => {
                                                        const isPureCredit = item.notes.includes('Abonados') && !item.notes.includes('Descontados');
                                                        return (
                                                            <div className="mt-1">
                                                                <Badge
                                                                    bg={isPureCredit ? 'success' : 'warning'}
                                                                    text={isPureCredit ? undefined : 'dark'}
                                                                    className={`bg-opacity-10 text-${isPureCredit ? 'success' : 'warning'} border border-${isPureCredit ? 'success' : 'warning'} border-opacity-25 text-wrap`}
                                                                    style={{ maxWidth: '220px', fontSize: '10px' }}
                                                                >
                                                                    {item.notes}
                                                                </Badge>
                                                            </div>
                                                        );
                                                    })()}
                                                </td>
                                                <td className="text-center pe-3" onClick={e => e.stopPropagation()}>
                                                    <div className="d-flex justify-content-center align-items-center gap-2 flex-wrap">
                                                        <Button
                                                            variant="outline-danger"
                                                            size="sm"
                                                            className="fw-bold d-flex align-items-center gap-1"
                                                            disabled={downloadingId === `${item.id}-pdf`}
                                                            onClick={() => downloadDocs(item, 'pdf')}
                                                            title="Descargar Comprobante en PDF"
                                                        >
                                                            📄 PDF
                                                        </Button>
                                                        <Button
                                                            variant="outline-success"
                                                            size="sm"
                                                            className="fw-bold d-flex align-items-center gap-1"
                                                            disabled={downloadingId === `${item.id}-excel`}
                                                            onClick={() => downloadDocs(item, 'excel')}
                                                            title="Descargar Detalle en Excel"
                                                        >
                                                            📊 Excel
                                                        </Button>
                                                        {item.invoice_url ? (
                                                            <Button
                                                                variant="primary"
                                                                size="sm"
                                                                className="fw-bold"
                                                                onClick={() => window.open(item.invoice_url, '_blank')}
                                                                title={`Ver Factura #${item.invoice_number || ''}`}
                                                            >
                                                                🧾 Ver Factura
                                                            </Button>
                                                        ) : (
                                                            <div className="d-inline-block">
                                                                <input
                                                                    type="file"
                                                                    id={`prov-invoice-upload-${item.id}`}
                                                                    style={{ display: 'none' }}
                                                                    accept="image/*,application/pdf"
                                                                    onChange={e => handleInvoiceUpload(item.id, e)}
                                                                />
                                                                <Button
                                                                    variant="outline-primary"
                                                                    size="sm"
                                                                    className="fw-bold"
                                                                    disabled={uploadingInvoiceId === item.id}
                                                                    onClick={() => document.getElementById(`prov-invoice-upload-${item.id}`).click()}
                                                                    title="Subir Factura de esta Liquidación"
                                                                >
                                                                    {uploadingInvoiceId === item.id ? (
                                                                        <Spinner animation="border" size="sm" />
                                                                    ) : (
                                                                        '📤 Subir Factura'
                                                                    )}
                                                                </Button>
                                                            </div>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>

                                            {isExpanded && (
                                                <tr>
                                                    <td colSpan="9" className="p-0 border-0">
                                                        <div className="bg-light p-4 border-bottom border-top">
                                                            {rowTrips[item.id] && !loadingTrips[item.id] && (() => {
                                                                const totals = getSettleTotals(item, rowTrips[item.id], rowAdvances[item.id]);
                                                                return (
                                                                    <div className="mb-3 bg-white p-3 rounded-3 border shadow-sm">
                                                                        <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
                                                                            <h6 className="fw-bold mb-0 text-muted small text-uppercase">
                                                                                Resumen Financiero de la Liquidación M-{String(item.id).padStart(3, '0')}
                                                                            </h6>
                                                                            {item.invoice_url && (
                                                                                <a
                                                                                    href={item.invoice_url}
                                                                                    target="_blank"
                                                                                    rel="noopener noreferrer"
                                                                                    className="btn btn-link btn-sm p-0 text-decoration-none fw-bold"
                                                                                >
                                                                                    Ver Factura Adjunta #{item.invoice_number || ''} ↗
                                                                                </a>
                                                                            )}
                                                                        </div>
                                                                        <Row className="g-2 text-center">
                                                                            <Col xs={6} md>
                                                                                <div className="p-2 border rounded bg-light">
                                                                                    <small className="text-muted fw-bold text-uppercase d-block" style={{ fontSize: '10px' }}>
                                                                                        Producción / Servicios
                                                                                    </small>
                                                                                    <span className="fw-bold">{formatCurrency(totals.bruto, totals.currency)}</span>
                                                                                </div>
                                                                            </Col>
                                                                            <Col xs={6} md>
                                                                                <div className="p-2 border rounded bg-light">
                                                                                    <small className="text-muted fw-bold text-uppercase d-block" style={{ fontSize: '10px' }}>
                                                                                        Cobrado en Efectivo (Cash)
                                                                                    </small>
                                                                                    <span className="fw-bold text-secondary">
                                                                                        {totals.efectivo > 0 ? `-${formatCurrency(totals.efectivo, totals.currency)}` : formatCurrency(0, totals.currency)}
                                                                                    </span>
                                                                                </div>
                                                                            </Col>
                                                                            <Col xs={6} md>
                                                                                <div className="p-2 border rounded bg-light">
                                                                                    <small className="text-muted fw-bold text-uppercase d-block" style={{ fontSize: '10px' }}>
                                                                                        Anticipos / Descuentos
                                                                                    </small>
                                                                                    <span className={totals.anticipos > 0 ? 'fw-bold text-danger' : 'text-muted'}>
                                                                                        {totals.anticipos > 0 ? `-${formatCurrency(totals.anticipos, totals.currency)}` : formatCurrency(0, totals.currency)}
                                                                                    </span>
                                                                                </div>
                                                                            </Col>
                                                                            {totals.abonos > 0 && (
                                                                                <Col xs={6} md>
                                                                                    <div className="p-2 border border-success border-opacity-25 rounded bg-success bg-opacity-10">
                                                                                        <small className="text-success fw-bold text-uppercase d-block" style={{ fontSize: '10px' }}>
                                                                                            Abonos / Reintegros
                                                                                        </small>
                                                                                        <span className="fw-bold text-success">
                                                                                            +{formatCurrency(totals.abonos, totals.currency)}
                                                                                        </span>
                                                                                    </div>
                                                                                </Col>
                                                                            )}
                                                                            <Col xs={6} md>
                                                                                <div className="p-2 border border-success border-opacity-25 rounded bg-success bg-opacity-10">
                                                                                    <small className="text-success fw-bold text-uppercase d-block" style={{ fontSize: '10px' }}>
                                                                                        Líquido a Pagar
                                                                                    </small>
                                                                                    <span className="fw-bold text-success">{formatCurrency(totals.liquido, totals.currency)}</span>
                                                                                </div>
                                                                            </Col>
                                                                        </Row>
                                                                    </div>
                                                                );
                                                            })()}

                                                            {rowAdvances[item.id] && rowAdvances[item.id].length > 0 && (() => {
                                                                const hasCredits = rowAdvances[item.id].some(a => (parseFloat(a.amount) || 0) < 0);
                                                                const hasDebits = rowAdvances[item.id].some(a => (parseFloat(a.amount) || 0) > 0);
                                                                const themeColor = hasCredits && !hasDebits ? 'success' : (hasCredits && hasDebits ? 'primary' : 'danger');
                                                                const sectionTitle = hasCredits && !hasDebits
                                                                    ? '🏆 Abonos y Reintegros Aplicados'
                                                                    : (hasCredits && hasDebits
                                                                        ? '⚖️ Ajustes Aplicados (Descuentos y Abonos)'
                                                                        : '🔻 Descuentos y Penalizaciones Aplicadas');
                                                                return (
                                                                    <div className={`mb-3 bg-${themeColor} bg-opacity-10 p-3 rounded-3 border border-${themeColor} border-opacity-25`}>
                                                                        <h6 className={`fw-bold mb-2 text-${themeColor} small text-uppercase`}>
                                                                            {sectionTitle}
                                                                        </h6>
                                                                        <Table size="sm" responsive className="bg-white mb-0 rounded-2 border">
                                                                            <thead className={`bg-${themeColor} bg-opacity-10 text-${themeColor} small fw-bold`}>
                                                                                <tr>
                                                                                    <th className="ps-3">Fecha</th>
                                                                                    <th>Concepto</th>
                                                                                    <th>Detalle / Referencia</th>
                                                                                    <th className="text-end pe-3">Monto Ajuste</th>
                                                                                </tr>
                                                                            </thead>
                                                                            <tbody className="small">
                                                                                {rowAdvances[item.id].map(adv => {
                                                                                    const rawAmt = parseFloat(adv.amount) || 0;
                                                                                    const isCredit = rawAmt < 0;
                                                                                    const notesStr = adv.notes || (isCredit ? 'Abono / Reintegro Booking' : 'Anticipo de caja');
                                                                                    const isPenalty =
                                                                                        !isCredit &&
                                                                                        (notesStr.toLowerCase().includes('disputa') ||
                                                                                            notesStr.toLowerCase().includes('booking') ||
                                                                                            notesStr.toLowerCase().includes('penalizaci'));
                                                                                    return (
                                                                                        <tr key={adv.id}>
                                                                                            <td className="ps-3 text-muted">
                                                                                                {adv.created_at ? new Date(adv.created_at).toLocaleDateString('es-CL') : 'N/A'}
                                                                                            </td>
                                                                                            <td className={`fw-bold ${isCredit ? 'text-success' : 'text-danger'}`}>
                                                                                                {isCredit ? '🏆 Abono / Reintegro Booking' : (isPenalty ? '⚠️ Penalización Booking' : '💵 Anticipo')}
                                                                                            </td>
                                                                                            <td>{notesStr}</td>
                                                                                            <td className={`text-end fw-bold pe-3 ${isCredit ? 'text-success' : 'text-danger'}`}>
                                                                                                {isCredit
                                                                                                    ? `+${formatCurrency(Math.abs(rawAmt), item.currency_code)}`
                                                                                                    : `-${formatCurrency(rawAmt, item.currency_code)}`}
                                                                                            </td>
                                                                                        </tr>
                                                                                    );
                                                                                })}
                                                                            </tbody>
                                                                        </Table>
                                                                    </div>
                                                                );
                                                            })()}

                                                            <h6 className="fw-bold mb-2 d-flex align-items-center gap-2 small text-uppercase text-muted">
                                                                <Search size={15} className="text-primary" /> Viajes Incluidos en esta Liquidación ({item.driver?.full_name || 'Conductor'})
                                                            </h6>

                                                            {loadingTrips[item.id] ? (
                                                                <div className="text-center py-3">
                                                                    <Spinner animation="border" variant="primary" size="sm" /> Cargando viajes...
                                                                </div>
                                                            ) : (
                                                                <div className="rounded-3 overflow-hidden border bg-white">
                                                                    <Table size="sm" responsive className="mb-0">
                                                                        <thead className="bg-light small">
                                                                            <tr>
                                                                                <th className="ps-3">ID</th>
                                                                                <th>Fecha</th>
                                                                                <th>Pasajero</th>
                                                                                <th>Estado</th>
                                                                                <th>Ruta (Origen → Destino)</th>
                                                                                <th className="text-end pe-3">Monto Liquidado</th>
                                                                            </tr>
                                                                        </thead>
                                                                        <tbody className="small">
                                                                            {(rowTrips[item.id] || []).map(t => (
                                                                                <tr key={t.id}>
                                                                                    <td className="ps-3 fw-bold text-muted">#{t.id}</td>
                                                                                    <td>
                                                                                        {t.scheduled_time
                                                                                            ? new Date(t.scheduled_time).toLocaleString('es-CL', {
                                                                                                  day: '2-digit',
                                                                                                  month: '2-digit',
                                                                                                  year: 'numeric',
                                                                                                  hour: '2-digit',
                                                                                                  minute: '2-digit'
                                                                                              })
                                                                                            : '-'}
                                                                                    </td>
                                                                                    <td className="fw-bold">{t.passenger_name || 'Pasajero'}</td>
                                                                                    <td>
                                                                                        {t.status === 'no_show' ? (
                                                                                            <Badge bg="danger" className="bg-opacity-10 text-danger border border-danger">
                                                                                                NO SHOW
                                                                                            </Badge>
                                                                                        ) : t.status === 'cancelled_paid' ? (
                                                                                            <Badge bg="info" className="bg-opacity-10 text-info border border-info">
                                                                                                CANCELADO C/CARGO
                                                                                            </Badge>
                                                                                        ) : (
                                                                                            <Badge bg="success" className="bg-opacity-10 text-success border border-success">
                                                                                                COMPLETADO
                                                                                            </Badge>
                                                                                        )}
                                                                                    </td>
                                                                                    <td className="text-truncate" style={{ maxWidth: '320px' }}>
                                                                                        <span className="text-danger fw-bold">A:</span> {t.pickup_address}
                                                                                        <br />
                                                                                        <span className="text-primary fw-bold">B:</span> {t.dropoff_address}
                                                                                    </td>
                                                                                    <td className="text-end fw-bold text-success pe-3">
                                                                                        {formatCurrency(
                                                                                            t.driver_earnings ||
                                                                                                ((t.provider_currency_code || t.local_currency_code || 'CLP') !== 'CLP'
                                                                                                    ? t.provider_cost_local
                                                                                                    : t.provider_cost_base) ||
                                                                                                0,
                                                                                            t.provider_currency_code || t.local_currency_code || item.currency_code
                                                                                        )}
                                                                                    </td>
                                                                                </tr>
                                                                            ))}
                                                                            {(rowTrips[item.id] || []).length === 0 && (
                                                                                <tr>
                                                                                    <td colSpan="6" className="text-center py-3 text-muted">
                                                                                        No hay viajes asociados a esta liquidación.
                                                                                    </td>
                                                                                </tr>
                                                                            )}
                                                                        </tbody>
                                                                    </Table>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    );
                                })}
                            </tbody>
                        </Table>
                    )}
                </Card.Body>
            </Card>
        </div>
    );
};

export default ProviderSettlementsView;
